import { nullable, num, object, oneOf, str, type FieldParser } from 'validation';

// Shared sub-object types + schemas for the scheduling_windows endpoints.
//
// The `schedules` / `references` element shapes are shared by IDENTITY: both are
// projections of one DB row (`scheduling_window_schedules`,
// `scheduling_window_references`) through a `jsonb_build_object` that the backend
// keeps in step across `windowDetailsSQL()`, the list query and
// `windowsListByZoneSQL()`. Same columns, same keys — so the element type lives
// once here and every endpoint imports it, exactly as `recurrence_definitions`
// shares `Recurrence`. The *envelopes* around them are NOT shared; see
// `getSchedulingWindows` for why the list row is its own type.
//
// Every key is always present, because `jsonb_build_object` emits a key per
// argument and writes SQL NULL as JSON `null` — so these are `nullable(...)`, not
// `optional(...)`. Only `scheduling_window_schedules.id` /
// `scheduling_window_id` are NOT NULL in the DB, and neither is projected.
//
// Validated wire data is immutable, so the types are deeply readonly.

export const REFERENCE_TYPES = ['open', 'closed'] as const;
export type ReferenceType = (typeof REFERENCE_TYPES)[number];

/**
 * One opening rule of a scheduling window.
 *
 * Shared between the read responses and the Save body: the backend round-trips
 * the same field names in both directions, and `validateAndNormalizeSchedule`
 * accepts an explicit `null` for every field (it normalizes absent / empty /
 * null alike). Fields are therefore declared **required-presence with a
 * permissive value** (`T | null`, not `T?`) so a `toSave()` projection that
 * forgets one fails to compile rather than silently dropping it.
 *
 * Loose at the type level on purpose — the backend owns the inter-field rules
 * (`start_time` ≠ `end_time`, `date_from` ≤ `date_until`, month 1–12, week of
 * month 1–5, day of week 1–7, `nth_of_month` −30…31 and both-or-neither). A
 * stricter client type would block valid sends if those rules ever loosen.
 *
 * `timezone` is an **override**: `null` means "inherit the window's timezone",
 * which the evaluator resolves on every evaluation (schedule override → the
 * schedule's own window → the evaluation context). The column is nullable with no
 * default, so this must stay nullable — see nap-frontend #1004.
 *
 * The five `*_path` fields are `zone_name|window_name` strings; the backend
 * resolves them to ids on write and renders them back from a join on read,
 * falling back to the bare id as text when the join finds nothing.
 */
export interface SchedulingWindowSchedule {
  readonly name: string | null;
  readonly description: string | null;
  /** `HH:MM:SS` wall clock (Postgres `time without time zone`). */
  readonly start_time: string | null;
  readonly end_time: string | null;
  /** Postgres `timestamp without time zone` — a wall clock, not an instant. */
  readonly date_from: string | null;
  readonly date_until: string | null;
  readonly month_from: number | null;
  readonly month_until: number | null;
  readonly week_of_month_from: number | null;
  readonly week_of_month_until: number | null;
  readonly day_of_week_from: number | null;
  readonly day_of_week_until: number | null;
  readonly nth_of_month_from: number | null;
  readonly nth_of_month_until: number | null;
  readonly timezone: string | null;
  readonly shift_open_days: number | null;
  readonly shift_if_closed_window_path: string | null;
  readonly nth_of_month_from_in_scheduling_window_path: string | null;
  readonly nth_of_month_until_in_scheduling_window_path: string | null;
  readonly open_during_scheduling_window_path: string | null;
  readonly closed_during_scheduling_window_path: string | null;
}

export const schedulingWindowScheduleSchema: FieldParser<SchedulingWindowSchedule> = object<SchedulingWindowSchedule>({
  name: nullable(str()),
  description: nullable(str()),
  start_time: nullable(str()),
  end_time: nullable(str()),
  date_from: nullable(str()),
  date_until: nullable(str()),
  month_from: nullable(num()),
  month_until: nullable(num()),
  week_of_month_from: nullable(num()),
  week_of_month_until: nullable(num()),
  day_of_week_from: nullable(num()),
  day_of_week_until: nullable(num()),
  nth_of_month_from: nullable(num()),
  nth_of_month_until: nullable(num()),
  timezone: nullable(str()),
  shift_open_days: nullable(num()),
  shift_if_closed_window_path: nullable(str()),
  nth_of_month_from_in_scheduling_window_path: nullable(str()),
  nth_of_month_until_in_scheduling_window_path: nullable(str()),
  open_during_scheduling_window_path: nullable(str()),
  closed_during_scheduling_window_path: nullable(str()),
});

/**
 * A link to another scheduling window, which either opens or closes this one
 * alongside itself.
 *
 * `scheduling_window_path` is nullable because it is built by concatenating the
 * referenced window's zone and name, and `scheduling_windows.zone_id` is itself
 * nullable — a zone-less window concatenates to SQL NULL. `type` is NOT NULL with
 * a CHECK constraint, so a drifted value degrades to `'open'` rather than failing
 * the row.
 *
 * A cycle is rejected by a DB trigger (`trg_..._no_cycle`) and by the route's own
 * check, so the server is the authority — the client does not gate on it.
 */
export interface SchedulingWindowReference {
  readonly scheduling_window_path: string | null;
  readonly type: ReferenceType;
}

export const schedulingWindowReferenceSchema: FieldParser<SchedulingWindowReference> = object<SchedulingWindowReference>({
  scheduling_window_path: nullable(str()),
  type: oneOf(REFERENCE_TYPES, 'open'),
});
